import * as React from 'react';
import { Children, isValidElement } from 'react';
import {
    SimpleForm as RaSimpleForm,
    SimpleFormProps,
    usePermissions,
    useRecordContext,
    useResourceContext,
} from 'react-admin';

import { canAccess } from '../canAccess';

import { Toolbar } from './Toolbar';

/**
 * Alternative to react-admin's <SimpleForm> that adds RBAC control to inputs
 *
 * To see an input, the user must have the permission to write the resource field:
 * { "action": "write", "resource": `${resource}.${source}` }
 *
 * SimpleForm also renders the delete button only if the user has the 'delete' permission.
 *
 * @example
 * import { SimpleForm } from '@react-admin/ra-rbac';
 *
 * const authProvider = {
 *     // ...
 *     getPermissions: () => Promise.resolve([
 *         // 'delete' is missing
 *         { action: ['list', 'edit'], resource: 'products' },
 *         { action: 'write', resource: 'products.reference' },
 *         { action: 'write', resource: 'products.width' },
 *         { action: 'write', resource: 'products.height' },
 *         // 'products.description' is missing
 *         { action: 'write', resource: 'products.thumbnail' },
 *         // 'products.image' is missing
 *     ]),
 * };
 *
 * const ProductEdit = () => (
 *     <Edit>
 *         <SimpleForm> // <-- RBAC SimpleForm
 *             <TextInput source="reference" />
 *             <TextInput source="width" />
 *             <TextInput source="height" />
 *             // not displayed
 *             <TextInput source="description" />
 *             // not displayed
 *             <TextInput source="image" />
 *             <TextInput source="thumbnail" />
 *             // no delete button
 *         </SimpleForm>
 *     </Edit>
 * );
 */
export const SimpleForm = (props: SimpleFormProps) => {
    const resource = useResourceContext();
    const record = useRecordContext();
    const { children, ...rest } = props;
    const { isLoading, permissions } = usePermissions();
    if (isLoading) {
        return null;
    }

    const renderChildren = (children) => {
        return Children.map(children, child => {
            if (typeof (child.props as any).source === 'undefined' && (child.props as any).children) {
                return (
                    <child.type {...child.props}>
                        {renderChildren((child.props as any).children)}
                    </child.type>
                )
            } else {
                return isValidElement(child) &&
                canAccess({
                    permissions,
                    action: 'write',
                    resource: `${resource}.${(child.props as any).source}`,
                    record,
                })
                    ? child
                    : null
                }
            }
        )
    }

    return (
        <RaSimpleForm toolbar={<Toolbar />} {...rest}>
            {renderChildren(children)}
        </RaSimpleForm>
    );
};

SimpleForm.propTypes = RaSimpleForm.propTypes;
